// frontend/src/layouts/MainLayout.js
import React, { useState, useEffect, useMemo } from 'react';
import { Box as MuiBox, ThemeProvider, createTheme, useTheme as useMuiTheme } from '@mui/material';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Collapse,
  Badge,
  Tooltip,
  useTheme,
  useMediaQuery,
  Button,
  Chip
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  FolderOpen,
  Translate,
  LibraryBooks,
  Assessment,
  People,
  Settings,
  AccountCircle,
  ExitToApp,
  ExpandLess,
  ExpandMore,
  Notifications,
  ChevronLeft,
  Search,
  AdminPanelSettings,
  Shield,
  DataUsage,
  Storage,
  Speed,
  FormatListBulleted,
  Business as BusinessIcon,
  Payments as PaymentIcon,
  SupervisorAccount as SuperAdminIcon,
  Api as ApiIcon,
  VpnKey as VpnKeyIcon,
  Palette as PaletteIcon,
  Psychology as PsychologyIcon,
  SupportAgent as SupportIcon,
  Webhook as WebhookIcon,
  MonitorHeart as MonitorHeartIcon,
  AttachMoney as AttachMoneyIcon,
  MarkEmailUnread as EmailTemplatesIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useOrganization } from '../contexts/OrganizationContext';
import { alpha } from '@mui/material/styles';
import OrganizationSwitcher from '../components/organization/OrganizationSwitcher';
import FreeTierBanner from '../components/organization/FreeTierBanner';
import NotificationBell from '../components/common/NotificationBell';
import Breadcrumbs from '../components/common/Breadcrumbs';
import { KeyboardShortcutButton } from '../components/common/KeyboardShortcutHint';

const drawerWidth = 280;

const MainLayout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAdmin, isProjectManager, isReviewer, isSuperAdmin } = useAuth();
  const { currentOrganization, membership, isOwner: isOrgOwner, isAdmin: isOrgAdmin } = useOrganization();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [anchorEl, setAnchorEl] = useState(null);
  const [resourcesOpen, setResourcesOpen] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(location.pathname.startsWith('/settings'));
  const [platformOpen, setPlatformOpen] = useState(location.pathname.startsWith('/platform'));

  // Global navigation keyboard shortcuts
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      // Don't fire shortcuts when typing in inputs
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) {
        return;
      }
      if (e.altKey) {
        switch (e.key) {
          case 'd': e.preventDefault(); navigate('/dashboard'); break;
          case 'p': e.preventDefault(); navigate('/projects'); break;
          case 'u': e.preventDefault(); navigate('/users'); break;
          case 'a': e.preventDefault(); navigate('/analytics'); break;
          case 'n':
            e.preventDefault();
            if (location.pathname.startsWith('/projects')) navigate('/projects/create');
            break;
          default: break;
        }
      }
    };
    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, [navigate, location.pathname]);

  const navigationItems = [
    {
      title: 'Dashboard',
      icon: <Dashboard />,
      path: '/dashboard',
      roles: ['super_admin', 'admin', 'project_manager', 'reviewer'],
    },
    {
      // Wave-07 · reviewer smart queue (cross-project SLA-sorted)
      title: 'My Queue',
      icon: <Speed />,
      path: '/my-queue',
      roles: ['super_admin', 'admin', 'project_manager', 'reviewer'],
    },
    {
      title: 'Text Translation',
      icon: <Translate />,
      path: '/text-translation',
      roles: ['super_admin', 'admin', 'project_manager', 'reviewer'],
    },
    {
      title: 'XLIFF Translation',
      icon: <Translate />,
      path: '/xliff-translation',
      roles: ['super_admin', 'admin', 'project_manager'],
    },
    {
      title: 'Projects',
      icon: <FolderOpen />,
      path: '/projects',
      roles: ['super_admin', 'admin', 'project_manager', 'reviewer'],
    },
    {
      title: 'Resources',
      icon: <LibraryBooks />,
      roles: ['super_admin', 'admin', 'project_manager', 'reviewer'],
      expandable: true,
      expanded: resourcesOpen,
      onToggle: () => setResourcesOpen(!resourcesOpen),
      children: [
        {
          title: 'Translation Memories',
          icon: <Storage />,
          path: '/translation-memories',
        },
        {
          title: 'Glossaries',
          icon: <LibraryBooks />,
          path: '/glossaries',
        },
      ],
    },
    {
      title: 'Users',
      icon: <People />,
      path: '/users',
      roles: ['super_admin', 'admin', 'project_manager'],
    },
    {
      title: 'Analytics',
      icon: <Assessment />,
      path: '/analytics',
      roles: ['super_admin', 'admin', 'project_manager'],
    },
    {
      title: 'Pipeline Health',
      icon: <MonitorHeartIcon />,
      path: '/analytics/pipeline-health',
      roles: ['super_admin', 'admin', 'project_manager'],
    },
    {
      // Wave-12 Phase 3 · CFO-facing spend breakdown
      title: 'Cost Attribution',
      icon: <AttachMoneyIcon />,
      path: '/analytics/cost-attribution',
      roles: ['super_admin', 'admin'],
    },
    {
      title: 'Support',
      icon: <SupportIcon />,
      path: '/support/tickets',
      roles: ['super_admin', 'admin', 'project_manager', 'reviewer'],
    },
    {
      title: 'Organization',
      icon: <BusinessIcon />,
      path: '/organizations/settings',
      roles: ['super_admin', 'admin', 'project_manager'],
    },
    {
      title: 'Settings',
      icon: <Settings />,
      roles: ['super_admin', 'admin'], // Admin can access settings
      expandable: true,
      expanded: settingsOpen,
      onToggle: () => setSettingsOpen(!settingsOpen),
      children: [
        {
          title: 'MT Configurations',
          icon: <Settings />,
          path: '/settings/mt-configurations',
        },
        {
          title: 'MT Usage Management',
          icon: <DataUsage />,
          path: '/settings/mt-usage',
        },
        {
          title: 'LLM Configurations',
          icon: <Settings />,
          path: '/settings/llm-configurations',
        },
        {
          title: 'Pipeline Admin',
          icon: <MonitorHeartIcon />,
          path: '/settings/pipeline-admin',
        },
        {
          title: 'LLM Prompt Templates',
          icon: <Settings />,
          path: '/settings/llm-prompt-templates',
        },
        {
          title: 'Auto-Approval Rules',
          icon: <Speed />,
          path: '/settings/auto-approval-rules',
        },
        {
          // Wave-03 · Semantic TM preview: paste source text, see blended trigram + semantic matches
          title: 'Semantic TM Preview',
          icon: <MonitorHeartIcon />,
          path: '/settings/tm-preview',
        },
        {
          // Wave-04 · Domain / industry catalog admin
          title: 'Domains',
          icon: <Speed />,
          path: '/settings/domains',
        },
        {
          // Wave-05 · Audit log dashboard
          title: 'Audit Log',
          icon: <MonitorHeartIcon />,
          path: '/settings/audit-log',
        },
        {
          // Wave-05 · PII scan (TM + glossary)
          title: 'PII Scan',
          icon: <Speed />,
          path: '/settings/pii-scan',
        },
        {
          // Wave-05 · SSO configuration
          title: 'SSO',
          icon: <MonitorHeartIcon />,
          path: '/settings/sso',
        },
        {
          // Wave-06 · Cross-project reviewer capacity dashboard
          title: 'Reviewer Capacity',
          icon: <Speed />,
          path: '/settings/reviewer-capacity',
        },
        {
          // Wave-09 · Item 2 · GDPR admin
          title: 'GDPR',
          icon: <MonitorHeartIcon />,
          path: '/settings/gdpr',
        },
        {
          // Wave-09 · Per-org TM leverage weights (memoQ-style analysis)
          title: 'TM Leverage Weights',
          icon: <Speed />,
          path: '/settings/tm-leverage-weights',
        },
        {
          title: 'Format Templates',
          icon: <FormatListBulleted />,
          path: '/settings/format-templates',
        },
        {
          title: 'QA Profiles',
          icon: <Shield />,
          path: '/settings/qa-profiles',
        },
        {
          title: 'API Keys',
          icon: <VpnKeyIcon />,
          path: '/settings/api-keys',
        },
        {
          // Wave-12 Phase 2 · Per-org white-label branding
          title: 'Branding',
          icon: <PaletteIcon />,
          path: '/settings/branding',
        },
        {
          // Wave-12 · Email template customization
          title: 'Email Templates',
          icon: <EmailTemplatesIcon />,
          path: '/settings/email-templates',
        },
        {
          title: 'Webhooks',
          icon: <WebhookIcon />,
          path: '/webhooks',
        },
      ],
    },
    {
      title: 'Platform Admin',
      icon: <SuperAdminIcon />,
      roles: ['super_admin'], // Only super admin
      expandable: true,
      expanded: platformOpen,
      onToggle: () => setPlatformOpen(!platformOpen),
      children: [
        {
          title: 'Dashboard',
          icon: <Dashboard />,
          path: '/platform',
        },
        {
          title: 'MT Engines',
          icon: <ApiIcon />,
          path: '/platform/mt-engines',
        },
        {
          title: 'LLM Providers',
          icon: <PsychologyIcon />,
          path: '/platform/llm-providers',
        },
        {
          title: 'Organizations',
          icon: <BusinessIcon />,
          path: '/platform/organizations',
        },
        {
          title: 'Platform Costs',
          icon: <PaymentIcon />,
          path: '/platform/costs',
        },
      ],
    },
  ];

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setDrawerOpen(!drawerOpen);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    const parts = name.split(' ');
    return parts
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getAvatarUrl = () => {
    if (!user?.avatar) return null;
    if (user.avatar.startsWith('http')) return user.avatar;
    // Strip /api suffix from base URL since avatar is served from root
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    const baseUrl = apiUrl.replace(/\/api\/?$/, '');
    return `${baseUrl}${user.avatar}?t=${Date.now()}`;
  };

  const getRoleDisplay = () => {
    // Super admin is a platform-wide role that takes precedence
    if (user?.role === 'super_admin') {
      return {
        label: 'Super Admin',
        color: 'warning',
        icon: <SuperAdminIcon sx={{ fontSize: 16, mr: 0.5 }} />
      };
    }

    // For regular users, show their organization membership role
    // This reflects their actual permissions in the current organization
    const orgRole = membership?.role;

    switch (orgRole) {
      case 'owner':
        return {
          label: 'Owner',
          color: 'error',
          icon: <AdminPanelSettings sx={{ fontSize: 16, mr: 0.5 }} />
        };
      case 'admin':
        return {
          label: 'Admin',
          color: 'error',
          icon: <AdminPanelSettings sx={{ fontSize: 16, mr: 0.5 }} />
        };
      case 'manager':
        return {
          label: 'Manager',
          color: 'primary',
          icon: null
        };
      case 'member':
        return {
          label: 'Member',
          color: 'info',
          icon: null
        };
      case 'reviewer':
        return {
          label: 'Reviewer',
          color: 'success',
          icon: null
        };
      default:
        // Fallback to global user role if no org membership
        switch (user?.role) {
          case 'admin':
            return {
              label: 'Administrator',
              color: 'error',
              icon: <AdminPanelSettings sx={{ fontSize: 16, mr: 0.5 }} />
            };
          case 'project_manager':
            return {
              label: 'Project Manager',
              color: 'primary',
              icon: null
            };
          case 'reviewer':
            return {
              label: 'Reviewer',
              color: 'success',
              icon: null
            };
          default:
            return {
              label: 'User',
              color: 'default',
              icon: null
            };
        }
    }
  };

  // Determine the effective role for menu visibility
  // Within an org context, use org membership role mapped to system-equivalent
  const getMenuRole = () => {
    if (user?.role === 'super_admin') return 'super_admin';
    if (membership?.role) {
      const orgRoleMap = { owner: 'admin', admin: 'admin', manager: 'project_manager', member: 'project_manager', reviewer: 'reviewer' };
      return orgRoleMap[membership.role] || user?.role;
    }
    return user?.role;
  };
  const menuRole = getMenuRole();

  const renderNavItem = (item, depth = 0) => {
    if (item.roles && !item.roles.includes(menuRole)) {
      return null;
    }

    const paddingLeft = depth > 0 ? 4 + (depth * 2) : 2;

    if (item.expandable) {
      return (
        <React.Fragment key={item.title}>
          <ListItem
            button
            onClick={item.onToggle}
            sx={{
              pl: paddingLeft,
              borderRadius: 1,
              mb: 0.5,
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.08),
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40, color: 'text.secondary' }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText 
              primary={item.title}
              primaryTypographyProps={{
                fontSize: '0.9rem',
                fontWeight: 500,
              }}
            />
            {item.expanded ? <ExpandLess /> : <ExpandMore />}
          </ListItem>
          <Collapse in={item.expanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children?.map((child) => renderNavItem(child, depth + 1))}
            </List>
          </Collapse>
        </React.Fragment>
      );
    }

    return (
      <ListItem
        button
        key={item.title}
        onClick={() => {
          navigate(item.path);
          if (isMobile) {
            setMobileOpen(false);
          }
        }}
        selected={isActive(item.path)}
        sx={{
          pl: paddingLeft,
          borderRadius: 1,
          mb: 0.5,
          '&:hover': {
            bgcolor: alpha(theme.palette.primary.main, 0.08),
          },
          '&.Mui-selected': {
            bgcolor: alpha(theme.palette.primary.main, 0.12),
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, 0.16),
            },
          },
        }}
      >
        <ListItemIcon 
          sx={{ 
            minWidth: 40,
            color: isActive(item.path) ? 'primary.main' : 'text.secondary',
          }}
        >
          {item.icon}
        </ListItemIcon>
        <ListItemText 
          primary={item.title}
          primaryTypographyProps={{
            fontSize: '0.9rem',
            fontWeight: isActive(item.path) ? 600 : 500,
            color: isActive(item.path) ? 'primary.main' : 'text.primary',
          }}
        />
      </ListItem>
    );
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar sx={{ px: 2 }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          width: '100%',
          gap: 1  // Add gap between elements
        }}>
          {/* Wave-12 Phase 2 · Sidebar logo. Uses org's uploaded logo
              when branding is enabled + a logoUrl is set; falls back to
              the default MTX/Lingo24 wordmark otherwise. */}
          <img
            src={(currentOrganization?.settings?.brandingEnabled && currentOrganization?.logoUrl) ? currentOrganization.logoUrl : '/Lingo24.svg'}
            alt={currentOrganization?.name || 'MTX'}
            style={{
              height: '45px',
              width: 'auto',
              objectFit: 'contain',
              flexShrink: 0
            }}
            onError={(e) => { e.currentTarget.src = '/Lingo24.svg'; }}
          />
          {/* Single text element */}
          <Typography 
            variant="body1" 
            sx={{ 
              color: 'text.secondary',
              fontSize: '0.875rem',
              whiteSpace: 'nowrap'  // Prevent text wrapping
            }}
          >
            Translation Portal
          </Typography>
        </Box>
      </Toolbar>
      <Divider />
      <Box sx={{ overflow: 'auto', flex: 1, px: 2, py: 1 }}>
        <List>
          {navigationItems.map((item) => renderNavItem(item))}
        </List>
      </Box>
      <Divider />
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            bgcolor: isAdmin ? 'error.50' : 'grey.100',
            borderRadius: 2,
            p: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            border: isAdmin ? '1px solid' : 'none',
            borderColor: isAdmin ? 'error.200' : 'transparent',
          }}
        >
          <Avatar
            src={getAvatarUrl()}
            sx={{
              bgcolor: isAdmin ? 'error.main' : 'primary.main',
              width: 36,
              height: 36
            }}
          >
            {isAdmin ? <Shield /> : getInitials(user?.firstName + ' ' + user?.lastName)}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" noWrap fontWeight={600}>
              {user?.firstName} {user?.lastName}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {user?.email}
            </Typography>
          </Box>
        </Box>
      </Box>
      {/* ADDED: Footer section */}
      <Divider />
      <Box sx={{ 
        p: 2, 
        textAlign: 'center',
        bgcolor: 'grey.50'
      }}>
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>
          <strong>MTX</strong> powered by <strong>DLX Technology</strong>
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          ©{new Date().getFullYear()} DLX Technology Co., Ltd
        </Typography>
      </Box>
    </Box>
  );

  const roleInfo = getRoleDisplay();

  // Wave-12 Phase 2 · Per-org MUI theme. When branding is enabled and a
  // valid hex primaryColor is set on the org, compose an override on top
  // of the root theme so every MUI Button / Chip / AppBar accent reflects
  // the org's brand. `useMuiTheme()` grabs the App-level theme so we
  // preserve all other tokens (spacing, shape, breakpoints, etc.).
  const rootTheme = useMuiTheme();
  const brandedTheme = useMemo(() => {
    const enabled = !!currentOrganization?.settings?.brandingEnabled;
    const color = currentOrganization?.settings?.customBranding?.primaryColor;
    if (!enabled || !/^#[0-9a-fA-F]{6}$/.test(color || '')) return rootTheme;
    return createTheme({
      ...rootTheme,
      palette: {
        ...rootTheme.palette,
        primary: { ...rootTheme.palette.primary, main: color },
      },
    });
  }, [rootTheme, currentOrganization?.settings?.brandingEnabled, currentOrganization?.settings?.customBranding?.primaryColor]);

  return (
    <ThemeProvider theme={brandedTheme}>
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />

      {/* AppBar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: drawerOpen ? `calc(100% - ${drawerWidth}px)` : '100%' },
          ml: { md: drawerOpen ? `${drawerWidth}px` : 0 },
          transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          bgcolor: 'background.paper',
          color: 'text.primary',
          boxShadow: 1,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="toggle drawer"
            onClick={handleDrawerToggle}
            edge="start"
            sx={{ mr: 2 }}
          >
            {isMobile ? <MenuIcon /> : (drawerOpen ? <ChevronLeft /> : <MenuIcon />)}
          </IconButton>

          {/* Organization Switcher */}
          <OrganizationSwitcher showTier={!isMobile} />

          <Box sx={{ flexGrow: 1 }} />

          {/* Notification Bell */}
          <NotificationBell />

          {/* User Menu */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={roleInfo.label}
              size="small"
              color={roleInfo.color}
              variant={isAdmin ? 'filled' : 'outlined'}
              icon={roleInfo.icon}
              sx={{ 
                mr: 1,
                fontWeight: isAdmin ? 600 : 400,
              }}
            />
            <IconButton
              onClick={handleProfileMenuOpen}
              sx={{
                p: 0.5,
                border: '2px solid',
                borderColor: isAdmin ? 'error.main' : 'divider',
              }}
            >
              <Avatar
                src={getAvatarUrl()}
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: isAdmin ? 'error.main' : 'primary.main'
                }}
              >
                {isAdmin ? <Shield sx={{ fontSize: 20 }} /> : getInitials(user?.firstName + ' ' + user?.lastName)}
              </Avatar>
            </IconButton>
          </Box>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            onClick={handleProfileMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{
              sx: { mt: 1.5, minWidth: 200 }
            }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                {user?.firstName} {user?.lastName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user?.email}
              </Typography>
              {isAdmin && (
                <Chip
                  label="Admin"
                  size="small"
                  color="error"
                  sx={{ mt: 0.5 }}
                  icon={<AdminPanelSettings sx={{ fontSize: 14 }} />}
                />
              )}
            </Box>
            <Divider />
            <MenuItem onClick={() => navigate('/profile')}>
              <ListItemIcon>
                <AccountCircle fontSize="small" />
              </ListItemIcon>
              <ListItemText>Profile</ListItemText>
            </MenuItem>
            {isAdmin && (
              <MenuItem onClick={() => navigate('/settings/mt-configurations')}>
                <ListItemIcon>
                  <Settings fontSize="small" />
                </ListItemIcon>
                <ListItemText>Settings</ListItemText>
              </MenuItem>
            )}
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <ExitToApp fontSize="small" />
              </ListItemIcon>
              <ListItemText>Logout</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: drawerOpen ? drawerWidth : 0 }, flexShrink: { md: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="persistent"
          open={drawerOpen}
          sx={{
            display: { xs: 'none', md: 'block' },
            width: drawerOpen ? drawerWidth : 0,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: drawerOpen ? `calc(100% - ${drawerWidth}px)` : '100%' },
          mt: '64px',
          transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Breadcrumbs />
        <FreeTierBanner />
        <Outlet />
        <KeyboardShortcutButton />
      </Box>
    </Box>
    </ThemeProvider>
  );
};

export default MainLayout;